import { CALCULATION_TYPES } from './constants'

/**
 * Extracts flat column values from the calculation results on a group node.
 * Matches each Orion response calculation by its `id` to the column instances,
 * then uses the type's extractValue (and extractExtraValues) to pull values.
 */
function extractColumns (calculations, columnInstanceMap) {
  const columns = {}
  if (!calculations || !columnInstanceMap) return columns

  for (const calc of calculations) {
    if (!calc.id) continue
    const col = columnInstanceMap.get(calc.id)
    if (!col) continue

    const typeDef = CALCULATION_TYPES[col.type]
    if (!typeDef) continue

    if (typeDef.extractValue) {
      columns[col.id] = typeDef.extractValue(calc)
    }
    if (typeDef.extractExtraValues) {
      Object.assign(columns, typeDef.extractExtraValues(calc, col.id))
    }
  }

  return columns
}

/**
 * Recursively transforms a calculated-group node into the row shape for the grid.
 * When hasMoreLevels is true, leaf groups are marked as expandable but not yet loaded.
 */
function transformGroupNode (group, columnInstanceMap, depth = 0, hasMoreLevels = false) {
  const leafCalcs = (group.calculations || []).filter(c => c.$type !== 'calculated-list')
  const nestedList = (group.calculations || []).find(c => c.$type === 'calculated-list')
  const children = nestedList
    ? (nestedList.calculations || [])
      .filter(c => c.$type === 'calculated-group')
      .map(child => transformGroupNode(child, columnInstanceMap, depth + 1, hasMoreLevels))
    : []

  const isLeafGrouping = children.length === 0
  const needsLazyChildren = isLeafGrouping && hasMoreLevels

  return {
    name: group.name || group.grouping || 'Unknown',
    grouping: group.grouping,
    groupId: group.groupId,
    groupEntityId: group.groupEntityId,
    depth,
    ...extractColumns(leafCalcs, columnInstanceMap),
    _childrenLoaded: !needsLazyChildren,
    data: needsLazyChildren ? [{ _placeholder: true, name: '' }] : (children.length > 0 ? children : undefined)
  }
}

/**
 * Transforms the gateway response into a tree of rows for the grid.
 * The gateway returns { data } where data is the raw Orion response with a calculations array.
 *
 * @param {object} response - The gateway response { data }
 * @param {{ columnInstances?: Array, hasMoreLevels?: boolean }} options
 */
export function transformResponse (response, options = {}) {
  if (!response) return { rows: [], metadata: null }

  const orionData = response.data
  const hasMoreLevels = options.hasMoreLevels ?? false
  const columnInstanceMap = new Map((options.columnInstances || []).map(c => [c.id, c]))

  if (!orionData?.calculations) {
    return { rows: [], metadata: null }
  }

  const topList = orionData.calculations.find(c => c.$type === 'calculated-list')
  if (!topList) {
    return { rows: [], metadata: null }
  }

  const groups = (topList.calculations || []).filter(c => c.$type === 'calculated-group')
  const rows = groups.map(group => transformGroupNode(group, columnInstanceMap, 0, hasMoreLevels))

  return { rows, metadata: null }
}

/**
 * Transforms a child response and returns just the child rows.
 * Used when lazily expanding a parent row.
 */
export function transformChildResponse (response, options = {}) {
  const { rows } = transformResponse(response, options)
  return rows
}
